'use client'

import * as Sentry from '@sentry/nextjs'
import { Check,Copy, Info } from 'lucide-react'
import React, { useEffect,useState } from 'react'

import { getChatPublicStatus, updateChatPublicStatus } from '@/api/agent/agentApi'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { markOnboardingTaskComplete } from '@/utils/onboarding'

interface ShareDialogProps {
  chatId: string
  isPublic: boolean
  onClose: () => void
  onPublicStatusChange: (newStatus: boolean) => void
}

export function ShareDialog({ chatId, isPublic, onClose, onPublicStatusChange }: ShareDialogProps) {
  const [publicAccess, setPublicAccess] = useState(isPublic)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [shareLink, setShareLink] = useState('')
  const [_apiResponseId, setApiResponseId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    setPublicAccess(isPublic)
    
    // Fetch current public status to get the API response ID
    const fetchCurrentStatus = async () => {
      try {
        const result = await getChatPublicStatus(chatId)
        if (result) {
          setApiResponseId(result.id)
          // Generate share link using API response ID
          const baseUrl = window.location.origin
          setShareLink(`${baseUrl}/chats/share/${result.id}`)
        }
      } catch (error) {
        console.error('Failed to fetch current public status:', error)
      }
    }
    
    if (isPublic) {
      fetchCurrentStatus()
    }
  }, [isPublic, chatId])

  const handlePublicAccessToggle = async (checked: boolean) => {
    setPublicAccess(checked)
    setError(null) // Clear any previous errors
    setSuccess(null) // Clear any previous success messages
    
    // Make API request immediately
    setLoading(true)
    try {
      const result = await updateChatPublicStatus(chatId, checked)
      
      if (result) {
        // Update the API response ID for the share link
        setApiResponseId(result.id)
        onPublicStatusChange(checked)
        
        // Generate new share link with the API response ID
        const baseUrl = window.location.origin
        const newShareLink = `${baseUrl}/chats/share/${result.id}`
        setShareLink(newShareLink)
        
        // Show success message
        if (checked) {
          setSuccess('Public access enabled! Share link created successfully.')
          try {
            if (typeof Sentry.metrics?.count === 'function') {
              Sentry.metrics.count('agent_share_chat', 1)
              void Sentry.flush(2000)
            }
            void markOnboardingTaskComplete('agent_share_chat')
          } catch (metricErr) {
            console.error('[Sentry] Failed to send agent_share_chat metric', metricErr)
          }
          // Auto-copy the link when public access is enabled
          setTimeout(() => {
            handleCopyLink(newShareLink)
          }, 500)
        } else {
          setSuccess('Public access disabled successfully.')
        }
      } else {
        // Revert the toggle if the API call failed
        setPublicAccess(isPublic)
        setError('Failed to update public access. Please try again.')
        console.error('Failed to update public access')
      }
    } catch (error) {
      console.error('Error updating public access:', error)
      setPublicAccess(isPublic)
      setError('An error occurred while updating public access. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyLink = async (linkToCopy?: string) => {
    const link = linkToCopy || shareLink
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy link:', error)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Share</DialogTitle>
          <DialogDescription>
            The generated link will allow anyone to view the chat conversation and actions taken by the agent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Public Access Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Public access
              </label>
            </div>
            <Switch
              checked={publicAccess}
              onCheckedChange={handlePublicAccessToggle}
              disabled={loading}
            />
          </div>

          {/* Share Link Section */}
          {publicAccess && shareLink && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Share link:</span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={shareLink}
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyLink()}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          )}

          {/* Success Display - Below the Share link form */}
          {success && (
            <Alert>
              <Check className="h-4 w-4" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>
                {success}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-4">
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
